/**
 * insertConverter — INSERT (Block reference) → CadPrimitive[], reimplementing
 * the transform composition over TYPED `@dxf-ai-editor/drawing-model` entities.
 *
 * Per `PLAN-plot-pdf.md` Appendix D / §7.7 we deliberately do NOT call
 * `scene3d/blockInsert.ts flattenBlockInsert` (it is WebGL/stringly coupled
 * and collapses non-uniform circles to a scalar radius). Instead we reuse the
 * MATH — scale→rotate→translate with base-point subtract, nested
 * scale-multiply / rotation-add — as a composed 2×3 affine ({@link Affine2D}),
 * which preserves the full transform.
 *
 * 3 pitfalls handled:
 *   1. **Non-uniform / mirror scale** carries the full 2×3 affine, so a
 *      non-uniform-scaled CIRCLE becomes a general ELLIPSE (the bezier eval
 *      maps the parametric circle through the affine) and a mirror (negative
 *      determinant) flips the arc orientation naturally.
 *   2. **ATTRIB / ATTDEF** are NOT flattened away — `walkBlock` resolves the
 *      INSERT's `attributes[]` against the block definition's ATTDEFs BY TAG +
 *      occurrence (Phase A / B, mirroring the typed reference
 *      `scene3d/adapters/typed/{attdefFromEntity,blockFromEntity}.ts`) and
 *      emits each as a `text-run` primitive: a variable ATTDEF renders its
 *      matching ATTRIB value (or the template `defaultValue` when unmatched —
 *      PDF-F3), a constant ATTDEF renders its own `defaultValue` (PDF-F2), a
 *      WORLD `overridePosition` ATTRIB is placed WITHOUT re-applying its own
 *      INSERT affine (PDF-F1 / PDF-F5), and duplicate-tag rows match Nth → Nth
 *      (PDF-F4). PDF-1 records them; PDF-2 renders.
 *   3. **Leaf Ellipse / Spline** are handled explicitly: transformed via the
 *      affine (the ellipse through bezier eval, the spline by mapping its cubic
 *      control points — PDF-5c). ATTDEF is NOT dispatched as a leaf — it is
 *      owned by the Phase A / B ATTRIB resolution above (pitfall 2).
 *
 * Cycle / depth guard mirrors AutoCAD's MAX_NESTED (50) — a re-entered block
 * stops descending (emits nothing rather than a placeholder; PDF-1 surfaces
 * missing geometry via the empty result, and the renderer already warns).
 */

import {
  coerceColor,
  colorToLegacyString,
  tableAsBlockReference,
} from "@dxf-ai-editor/drawing-model";
import type {
  AttdefEntity,
  BlockInsertEntity,
  BlockChildEntity,
  BlockInsertAttribute,
  BlockDefinition,
} from "@dxf-ai-editor/drawing-model";

import { evaluateBlock } from "@dxf-ai-editor/block-evaluator";

import { resolveColorHex } from "../../../scene3d/rendering/colorResolution";
import type {
  CadConvertContext,
  CadPrimitive,
  PathSegment,
  PathPrimitive,
  Point2,
  PlotStyle,
} from "../cadPrimitive";
import { arcSpansToBeziers } from "./arcBezier";
import {
  appendTransformedBulgeSpan,
  transformedCircle,
} from "./transformedArc";
import {
  heavyPolylineBandPath,
  heavyPolylinePaths,
} from "./heavyPolylineConverter";
import { bandPathPrimitive, lwpolylineBand } from "./widePolylineBand";
import { oleFramePlotCorners } from "./oleFrameConverter";
import {
  apply,
  applyLinear,
  compose,
  insertMatrix,
  IDENTITY,
  type Affine2D,
} from "./affine2d";
import { entityAciForCtb, resolvePlotStyle } from "./resolveStyle";
import { splineBezierSegments } from "./splineConverter";
import { isDefaultOcs, ocsToWcs2d } from "./ocs";
import {
  attdefFlag,
  emitConstantAttdef,
  emitOrphanAttribute,
  emitOrphanAttributes,
  emitVariableAttdef,
  foldScalarRotationDeg,
  resolveChildLayer,
} from "./insertAttributes";
import { textChildToRun } from "./insertTextChild";

const DEG_TO_RAD = Math.PI / 180;
const MAX_DEPTH = 50;

/**
 * Build the OCS→WCS-XY transform of a BLOCK (INSERT) as an outer {@link Affine2D}
 * (PDF-5d). INSERT is one of the 3 model entities carrying an `extrusion` (OCS
 * normal). When non-default, the INSERT's whole placement (insertion point +
 * scale + rotation) is authored in the OCS plane; we compose the OCS basis map
 * as the OUTERMOST transform so every child geometry the recursive walk emits
 * is lifted to WCS. The map projected to XY is the pure linear part
 * `[Ax.x Ay.x; Ax.y Ay.y]` (its translation is the WCS image of the OCS origin,
 * which is 0 for an origin-anchored basis — the insertion-point translation
 * already lives inside `insertMatrix`, expressed in OCS, then lifted by this
 * linear map). Default (0,0,1) normal → IDENTITY (no behaviour change).
 */
function ocsOuterMatrix(
  extrusion: BlockInsertEntity["extrusion"] | undefined,
): Affine2D {
  if (isDefaultOcs(extrusion)) return IDENTITY;
  const lift = ocsToWcs2d(extrusion);
  // Columns are the WCS-XY images of the OCS unit X / Y axes (origin maps to 0).
  const ex = lift(1, 0); // Ax projected
  const ey = lift(0, 1); // Ay projected
  return { a: ex.x, b: ex.y, c: ey.x, d: ey.y, e: 0, f: 0 };
}

/**
 * PDF-5d/F6 scalar twin — the SCALAR rotation (deg) + handedness flip an OCS
 * (extrusion) lift contributes to block-local attribute TEXT, so the emitted
 * text-run angle follows the SAME OCS basis its ANCHOR position rides through
 * {@link ocsOuterMatrix}. `resolveAttrPlacement` moves the anchor through the
 * composed matrix (which includes `ocsOuter`) but builds the glyph angle from a
 * SCALAR rotation SUM carried authored — NEVER inferred from the composed
 * matrix, because a composed single-axis mirror is genuinely ambiguous (see
 * `insertAttributes.ts foldScalarRotationDeg`). So the OCS basis must feed that
 * sum too; otherwise a tilted-normal INSERT (e.g. `(0.1,0,0.995)`, whose OCS X
 * axis projects along WCS Y) rotates the geometry + anchor ~90° while the label
 * stays at the authored angle (@codex #480).
 *
 * The contribution is the rotation of the OCS X-axis image (`atan2(ex.y, ex.x)`)
 * plus a handedness flip when the projected basis is LEFT-handed (det < 0 — e.g.
 * a `(0,0,-1)` X-mirror). The caller folds that flip into the Y scale-sign:
 * once the X-axis angle is extracted via `atan2`, the residual reflection of ANY
 * left-handed basis is the Y-axis mirror (`R(−θ)·Reflection ≡ diag(1,−1)`), so a
 * `(0,0,-1)` extrusion reads as 180° + a Y-mirror — NOT an X-mirror — keeping
 * the double-negative point-reflection fold consistent with a negative INSERT
 * scale (@codex #507). Default (0,0,1) normal → `{ 0, false }` (byte-identical).
 * EXACT when the projected basis is a pure
 * rotation (incl. uniform scale / mirror); the residual projection foreshorten
 * under a tilted normal is the SAME non-uniform shear the scalar convention
 * defers everywhere (module header of `insertAttributes.ts resolveAttrPlacement`).
 */
function ocsScalarContribution(
  extrusion: BlockInsertEntity["extrusion"] | undefined,
): { rotationDeg: number; flipHandedness: boolean } {
  const m = ocsOuterMatrix(extrusion);
  const det = m.a * m.d - m.b * m.c;
  return {
    rotationDeg: (Math.atan2(m.b, m.a) * 180) / Math.PI,
    flipHandedness: det < 0,
  };
}

/**
 * PDF-F6 (@codex #507 round-3) — compose a block-LOCAL authored rotation
 * (`localRotationDeg`, e.g. `entity.rotation` / `child.rotation`) UNDER an OCS
 * lift's own scalar contribution (`ocsScalar`, {@link ocsScalarContribution}).
 *
 * A RIGHT-handed OCS basis (`flipHandedness` false) is a PURE rotation
 * `R(β)` (β = `ocsScalar.rotationDeg`), which composes with the local
 * rotation by plain ADDITION: `R(β)·R(θ) = R(β+θ)`.
 *
 * A LEFT-handed (mirrored) OCS basis decomposes EXACTLY as `R(β)·mirrorY`
 * (module header above — the atan2 of the OCS X-axis image extracts β, and
 * the residual is ALWAYS the Y-axis reflection for an orthonormal frame).
 * Composing the block-local rotation θ goes INSIDE that reflection —
 * `R(β)·mirrorY·R(θ)·S` — and a reflection CONJUGATES any rotation to its
 * INVERSE (`F·R(θ) ≡ R(−θ)·F` for every reflection `F`; standard O(2)
 * dihedral-group identity — Autodesk OCS positions/orientations are always
 * orthonormal, so this is exact here, matching ezdxf's `OCS.to_wcs` /
 * `Text.transform()` full-vector-transform convention for extrusion, as
 * opposed to the UNRELATED "keep the raw authored sum" carve-out
 * `foldScalarRotationDeg` documents for an INSERT's OWN negative-scale
 * mirror). So the mirror commutes θ to `R(−θ)`, giving
 * `R(β)·R(−θ)·mirrorY = R(β−θ)·mirrorY`: the composed angle REFLECTS
 * (`β − θ`), it does not add. A straight sum (`θ + β`) flipped a
 * mirrored-extrusion ATTDEF/ATTRIB baseline 180° off whenever θ ≠ 0
 * (@codex finding on PR #507, insertConverter.ts:393 pre-fix).
 *
 * The residual `mirrorY` sign is folded into `scaleYNeg` at each call site
 * (unaffected by this helper — the @codex #507 round-2 fix); this is ONLY
 * the rotation angle.
 */
function composeOcsRotationDeg(
  localRotationDeg: number,
  ocsScalar: { rotationDeg: number; flipHandedness: boolean },
): number {
  return ocsScalar.flipHandedness
    ? ocsScalar.rotationDeg - localRotationDeg
    : localRotationDeg + ocsScalar.rotationDeg;
}

/**
 * Per-INSERT dynamic-block instance state — the customProperties /
 * visibilityState that drive parameter actions + visibility filtering. Mirrors
 * the renderer's `InsertInstanceLike` (apps/web/src/app/scene/blockRender.tsx).
 * Undefined for the implicit instance of a static block (no dynamicMetadata).
 */
interface BlockInstanceLike {
  customProperties?: Readonly<Record<string, string | number | boolean>>;
  visibilityState?: string;
}

/** Internal recursion state. */
interface InsertWalkState {
  ctx: CadConvertContext;
  /** Composed local→world affine. */
  matrix: Affine2D;
  /** Cycle-detection chain of block names. */
  visited: ReadonlySet<string>;
  depth: number;
  /** Owning top-level INSERT id (for primitive ids). */
  parentId: string;
  /** Resolved colour of the enclosing INSERT for BYBLOCK children. */
  blockColor: string | null;
  /**
   * PDF-F16 — the enclosing INSERT's ACI (1..256) for a BYBLOCK child's CTB
   * lookup, or null when it cannot be determined. A ByBlock child's OWN colour
   * has no index (its resolved hex lost the ACI), so its do-not-plot / screening
   * CTB entry follows the INSERT's ACI. Propagates through nested INSERTs the
   * same way as `blockColor` (a nested ByBlock INSERT inherits the parent ACI,
   * an explicit / ByLayer nested INSERT contributes its own).
   */
  blockAci: number | null;
  /**
   * Resolved linetype name of the enclosing INSERT for BYBLOCK children
   * (PDF-5c). A ByBlock child inherits this dash pattern. Null → Continuous.
   */
  blockLinetype: string | null;
  /**
   * Resolved OCG layer of the enclosing INSERT (PDF-6.2a). A block child on
   * layer "0" inherits THIS layer; a child on any other layer keeps its own —
   * the AutoCAD rule, matching `state/blockReducerHelpers.ts resolveChildLayer`.
   * For a nested INSERT the inherited layer propagates: a nested INSERT on "0"
   * takes its parent's resolved layer, then ITS children inherit that.
   */
  insertLayer: string;
  /**
   * The INSERT instance's dynamic params (customProperties / visibilityState).
   * `walkBlock` feeds these to `evaluateBlock` when the block carries
   * dynamicMetadata so the PLOT shows the user-resolved geometry, not the
   * authored default / hidden state (#177 P2). Undefined → static walk.
   */
  instance: BlockInstanceLike;
  /**
   * PDF-F1..F4 — the OWNING INSERT's `attributes[]` (top-level INSERT:
   * `entity.attributes`; a nested `Block` child: the child's OWN
   * `attributes[]`). `walkBlock` resolves these against the block definition's
   * ATTDEFs BY TAG + occurrence (Phase A / B, mirroring the typed reference
   * `scene3d/adapters/typed/blockFromEntity.ts flattenTypedInsert`), so an
   * unmatched variable ATTDEF falls back to its `defaultValue` (F3), a constant
   * ATTDEF renders its own `defaultValue` (F2), duplicate-tag rows match Nth →
   * Nth (F4) and a genuinely orphaned row still surfaces (data preservation).
   */
  attributes: readonly BlockInsertAttribute[];
  /**
   * PDF-F1 / PDF-F5 — the accumulated OUTER (residual) transform applied to a
   * WORLD `overridePosition` ATTRIB at THIS level (typed reference oracle:
   * `attdefFromEntity.ts AttribOuterTransform`). `overridePosition` is a WORLD
   * point (`blockInsertAttributeSchema` INVARIANT / `attdefFromEntity.ts:41-63`)
   * that must NOT ride the ATTRIB's OWN immediate INSERT affine (that would
   * double-transform it — an ATTRIB at world (102,103) under an INSERT at
   * (100,100) must print at (102,103), not (202,203)); it STILL rides everything
   * ABOVE that affine:
   *   - top-level single INSERT      → IDENTITY (the world point is final);
   *   - MINSERT non-base grid cell   → a pure world translation by the cell's
   *     grid offset (the stored ATTRIB positions the base cell — each repeated
   *     cell shifts the label by the same step the geometry moves);
   *   - nested `Block` recursion     → the ENCLOSING block's local→world map
   *     (its `baseMatrix`), because a nested INSERT's ATTRIB group-10 lives in
   *     that enclosing block's frame, not the drawing WCS (PDF-F5).
   * A block-LOCAL (unset `overridePosition`) ATTRIB/ATTDEF ignores this and
   * rides the full per-level `baseMatrix` like every other block child.
   */
  outer: Affine2D;
  /**
   * PDF-F10 — the accumulated AUTHORED INSERT-rotation SUM (deg) + scale-sign
   * products for the transform block-LOCAL attribute text rides (`matrix` →
   * `baseMatrix`). Carried from the walk (the oracle's `t.rotationDeg` /
   * `t.scale` signs) rather than inferred from the composed `matrix`, because a
   * composed single-axis mirror is AMBIGUOUS (see `insertAttributes.ts
   * foldScalarRotationDeg`). Accumulate per nesting level exactly like the typed
   * oracle: `rotationDeg + child.rotation`, `scale.x · child.scaleX` (sign).
   */
  rotationSumDeg: number;
  scaleXNeg: boolean;
  scaleYNeg: boolean;
  /**
   * PDF-F10 — the folded scalar rotation (deg) of the OUTER residual (`outer`) a
   * WORLD `overridePosition` rides: 0 at the top level / MINSERT cell (the
   * residual is identity / a pure translation), the ENCLOSING block's scalar
   * rotation for a nested INSERT.
   */
  outerScalarRotationDeg: number;
  /**
   * @codex #507 round-4 — the accumulated OCS-basis-only handedness (XOR of
   * every level's `ocsScalarContribution(...).flipHandedness` from the root
   * INSERT down to THIS level, exclusive of the UNRELATED plain-scale-sign
   * mirror tracked by `scaleXNeg`/`scaleYNeg`). `composeOcsRotationDeg`
   * REFLECTS a composed rotation under a mirrored OCS basis and ADDS under a
   * proper one (module header above) — that decision must key off whether the
   * transform ALREADY ACCUMULATED so far (this field, BEFORE folding in a new
   * child) is OCS-mirrored, not off the new child's OWN OCS handedness alone.
   * A single-level OCS mirror (top-level `insertToPrimitives`, or a nested
   * child under an unmirrored OUTER) starts from `ocsMirrorParity = false`, so
   * `composeOcsRotationDeg(childLocal, {state.rotationSumDeg, false})` reduces
   * to the plain `state.rotationSumDeg + childLocal` sum — byte-identical to
   * every existing (single-OCS-level) test. Two STACKED OCS mirrors (an
   * already-mirrored OUTER nesting an OCS-tilted INNER) is where the two
   * conventions diverge: reflection conjugates a following rotation to its
   * inverse (`F·R(θ) ≡ R(−θ)·F`), so the SECOND mirror must SUBTRACT the
   * child's own composed angle from the accumulated sum, and the two mirrors
   * cancel back to a PROPER (non-mirrored) parity for anything nested deeper
   * — exactly like `scaleXNeg`/`scaleYNeg`'s independent per-axis XOR chain,
   * but scoped to ONLY the OCS-basis mirror source (an INSERT's own
   * negative-scale mirror keeps the RAW accumulated sum per
   * `insertAttributes.ts foldScalarRotationDeg` — a DIFFERENT, intentionally
   * non-reflecting AutoCAD convention for block-local attribute text under a
   * user-authored mirror, module header above).
   */
  ocsMirrorParity: boolean;
}

export function insertToPrimitives(
  entity: BlockInsertEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  const out: CadPrimitive[] = [];

  // The INSERT's own resolved colour feeds BYBLOCK resolution of its children.
  // FIX (#177 P2): resolve the INSERT's OWN colour to a concrete hex first.
  // The raw `entity.color` may be a keyword ("ByLayer" / "ByBlock") which a
  // BYBLOCK child's `resolveColorHex` cannot interpret as a literal parent
  // colour — so a BYBLOCK child would mis-resolve. Resolving here means the
  // child inherits the INSERT's *resolved* layer/block colour (AutoCAD parity).
  // EPIC-COLOR-UNION S1 — project the typed colour union to the legacy string
  // the plot resolvers consume (byte-identical for ByLayer/ByBlock/index).
  const insertHex = resolvePlotRgbHex(
    colorToLegacyString(coerceColor(entity.color)),
    entity.layer,
    ctx,
  );

  // PDF-F16: resolve the INSERT's OWN ACI (1..256) for the BYBLOCK CTB chain.
  // `insertHex` above collapsed the ACI to a hex; a BYBLOCK child needs the
  // INDEX to hit the INSERT colour's do-not-plot / screening CTB entry. A
  // top-level BYBLOCK INSERT has no enclosing block, so `resolveColorHex` above
  // resolved it through the LAYER colour (root BYBLOCK ≡ BYLAYER). Resolve its
  // CTB ACI the SAME way — via the LAYER index — so a BYBLOCK child honours the
  // INSERT's (layer-derived) do-not-plot / screening entry instead of dropping
  // it. Pass that layer ACI as the BYBLOCK fallback: `entityAciForCtb` returns
  // the fallback ONLY for the BYBLOCK / "0" case, so an explicit-ACI / ByLayer
  // INSERT ignores it and resolves its own index (byte-identical to before).
  const rootLayerAci = entityAciForCtb(
    "BYLAYER",
    entity.layer ?? "0",
    ctx.layerColors,
    null,
  );
  const insertAci = entityAciForCtb(
    colorToLegacyString(coerceColor(entity.color)),
    entity.layer ?? "0",
    ctx.layerColors,
    rootLayerAci,
  );

  // PDF-5c: resolve the INSERT's OWN linetype for the BYBLOCK chain. A ByBlock
  // child inherits the INSERT's dash pattern; a ByLayer INSERT contributes its
  // layer's linetype; an explicit token passes through. Continuous → null so
  // the registry lookup short-circuits to solid.
  const insertLinetype = resolveInsertLinetype(entity, ctx);

  // PDF-5d OCS/extrusion: a non-default extrusion lifts the entire OCS-authored
  // placement to WCS. Compose the OCS basis as the OUTERMOST transform so every
  // descended child is mapped through it. Default (0,0,1) → IDENTITY, so the
  // composed matrix equals the bare insertMatrix (existing INSERT tests stay
  // byte-identical).
  const ocsOuter = ocsOuterMatrix(entity.extrusion);
  // PDF-F6 scalar twin — the SCALAR rotation / handedness the OCS lift adds to
  // block-local attribute text, so its emitted angle rides the SAME basis as
  // its anchor (@codex #480). {0,false} for the default normal (byte-identical).
  const ocsScalar = ocsScalarContribution(entity.extrusion);
  const matrix = compose(
    ocsOuter,
    insertMatrix(
      { x: entity.insertionPoint.x, y: entity.insertionPoint.y },
      entity.scaleX,
      entity.scaleY,
      entity.rotation,
      { x: 0, y: 0 }, // block base subtracted inside walkBlock per-definition
    ),
  );

  // MINSERT grid (DXF 70/71 = columnCount/rowCount, 44/45 = column/row
  // spacing). A plain INSERT is a 1×1 grid → a single cell at offset (0,0),
  // byte-identical to the pre-fix behaviour. Each cell composes a LOCAL-space
  // translation (col·columnSpacing, row·rowSpacing) onto the base insert
  // matrix BEFORE walkBlock subtracts the block base point — matching the
  // renderer's `baseMatrix.translate({x: col*columnSpacing, y: row*rowSpacing})`
  // (apps/web/src/app/scene/blockRender.tsx), i.e. the offset is applied in the
  // INSERT's rotated/scaled frame (AutoCAD MINSERT convention).
  const columnCount = Math.max(1, Math.round(entity.columnCount ?? 1));
  const rowCount = Math.max(1, Math.round(entity.rowCount ?? 1));
  const columnSpacing = entity.columnSpacing ?? 0;
  const rowSpacing = entity.rowSpacing ?? 0;

  for (let row = 0; row < rowCount; row++) {
    for (let col = 0; col < columnCount; col++) {
      const isBaseCell = col === 0 && row === 0;
      // Local-space cell offset → composed on the right so it is applied in
      // the block's local frame (before the insert's rotate/scale). compose
      // with a pure translation matches BlockMatrix.translate exactly.
      const cellMatrix = isBaseCell
        ? matrix
        : compose(matrix, {
            a: 1,
            b: 0,
            c: 0,
            d: 1,
            e: col * columnSpacing,
            f: row * rowSpacing,
          });

      // PDF-F1 — the per-cell OUTER residual for a WORLD `overridePosition`
      // ATTRIB (`InsertWalkState.outer`). A stored ATTRIB positions the BASE
      // cell, so the base cell's residual is IDENTITY (the world point is
      // already final); each repeated MINSERT cell shifts the label by the
      // SAME world step the geometry moves — the cell's local grid offset
      // pushed through the block's own rotate+scale (`applyLinear(matrix, …)`,
      // the linear part only — no basePoint subtract / translation), matching
      // the typed reference `blockFromEntity.ts`'s per-cell `cellOuter`.
      let cellOuter: Affine2D = IDENTITY;
      if (!isBaseCell) {
        const worldOffset = applyLinear(matrix, {
          x: col * columnSpacing,
          y: row * rowSpacing,
        });
        cellOuter = { a: 1, b: 0, c: 0, d: 1, e: worldOffset.x, f: worldOffset.y };
      }

      // (Pitfall 2) — ATTRIB / ATTDEF text runs are now resolved INSIDE
      // `walkBlock` (Phase A/B against the block definition's ATTDEFs), PER
      // CELL, so a MINSERT grid's stamp text is repeated with the geometry.
      walkBlock(out, entity.blockName, {
        ctx,
        matrix: cellMatrix,
        visited: new Set<string>(),
        depth: 0,
        parentId: entity.id,
        blockColor: insertHex,
        blockAci: insertAci,
        blockLinetype: insertLinetype,
        insertLayer: entity.layer ?? "0",
        instance: {
          customProperties: entity.customProperties,
          ...(entity.visibilityState !== undefined
            ? { visibilityState: entity.visibilityState }
            : {}),
        },
        attributes: entity.attributes ?? [],
        outer: cellOuter,
        // PDF-F10 — the top INSERT's OWN authored rotation / scale signs drive
        // the block-local attribute text's scalar rotation (every MINSERT cell
        // shares them; the grid offset is a pure translation). PDF-F6 — the OCS
        // lift composes its own basis rotation / handedness (`ocsScalar`) so the
        // glyph angle follows the anchor under a tilted / mirrored extrusion —
        // ADDS under a proper (right-handed) OCS rotation, REFLECTS under a
        // mirrored (left-handed) one (`composeOcsRotationDeg`, @codex #507
        // round-3). The OUTER residual is identity (base cell) or a pure
        // translation (repeated cell), so its scalar rotation is 0 for every cell.
        rotationSumDeg: composeOcsRotationDeg(entity.rotation, ocsScalar),
        scaleXNeg: entity.scaleX < 0,
        // PDF-F6 — the OCS handedness flip toggles the Y sign: after extracting
        // the OCS X-axis angle (`rotationDeg = atan2(ocsY, ocsX)`), a left-handed
        // basis's residual mirror is ALWAYS the Y axis (R(−θ)·Reflection ≡
        // diag(1,−1)), so a `(0,0,−1)` extrusion (180° + Y-mirror) folds
        // correctly with a negative INSERT scale — putting it on X folded the
        // scalar angle backwards (@codex #507).
        scaleYNeg: entity.scaleY < 0 !== ocsScalar.flipHandedness,
        outerScalarRotationDeg: 0,
        // @codex #507 round-4 — the top INSERT's OWN OCS handedness seeds the
        // accumulated OCS-mirror-only parity a nested child's composition
        // reflects/adds against (`InsertWalkState.ocsMirrorParity` above).
        ocsMirrorParity: ocsScalar.flipHandedness,
      });
    }
  }

  return out;
}

/**
 * PDF-F4/F17/F18 — resolve a VARIABLE ATTDEF child's AUTHORED occurrence index
 * (which same-tag ATTRIB row it consumes) from the census keyed by authored id.
 *
 *   - a static / first-array-copy child carries its authored id verbatim → its
 *     authored occurrence (F4: duplicate-tag Nth→Nth; F17: a surviving ATTDEF
 *     keeps its own index even when an earlier same-tag sibling was hidden);
 *   - an Array-generated EXTRA copy carries a `${sourceId}#arr<n>` synthetic id
 *     (block-evaluator `evaluator.ts` mints these so the byId map stays
 *     collision-free); recover the SOURCE ATTDEF's occurrence so every arrayed
 *     copy reuses the authored row instead of running past the end into a
 *     template default (F18). The `#arr<n>` suffix is the evaluator's documented
 *     copy-id contract; a mismatch degrades gracefully to the row-0 fallback,
 *     which is still correct for the common single-ATTDEF array;
 *   - anything else (a synthesized child with no authored lineage) → 0.
 */
function authoredOccurrenceForChild(
  childId: string,
  authoredVariableOccurrenceById: ReadonlyMap<string, number>,
): number {
  const direct = authoredVariableOccurrenceById.get(childId);
  if (direct !== undefined) return direct;
  const arrayCopy = /^(.*)#arr\d+$/.exec(childId);
  if (arrayCopy) {
    const source = authoredVariableOccurrenceById.get(arrayCopy[1]!);
    if (source !== undefined) return source;
  }
  return 0;
}

/**
 * PDF-F2/F4/F17/F18 — the AUTHORED ATTDEF census of a block definition, keyed
 * for the Phase A/B match + orphan suppression. Built from the FULL authored
 * `def.entities` (BEFORE the dynamic-visibility filter `resolveBlockChildren`
 * applies), so:
 *   - `definitionVariableCount` — per tag, how many VARIABLE ATTDEF occurrences
 *     the definition authors (bounds how many `attributes[]` rows the templates
 *     claim; the orphan pass suppresses a hidden ATTDEF's row via this count);
 *   - `definitionConstantTags` — tags carried by a CONSTANT ATTDEF (whose value
 *     is authoritative — any stray same-tag row is swallowed, not orphaned);
 *   - `authoredVariableOccurrenceById` — every VARIABLE ATTDEF's AUTHORED
 *     occurrence index BY id, so a surviving ATTDEF whose earlier same-tag
 *     sibling was HIDDEN still matches its OWN row (F17) and an Array copy
 *     recovers the source row (F18).
 */
interface AttdefCensus {
  definitionVariableCount: ReadonlyMap<string, number>;
  definitionConstantTags: ReadonlySet<string>;
  authoredVariableOccurrenceById: ReadonlyMap<string, number>;
}

function buildAttdefCensus(def: BlockDefinition): AttdefCensus {
  const definitionVariableCount = new Map<string, number>();
  const definitionConstantTags = new Set<string>();
  const authoredVariableOccurrenceById = new Map<string, number>();
  for (const authored of def.entities) {
    if (authored.type !== "Attdef") continue;
    if (attdefFlag(authored, "constant")) {
      definitionConstantTags.add(authored.tag);
    } else {
      const occ = definitionVariableCount.get(authored.tag) ?? 0;
      authoredVariableOccurrenceById.set(authored.id, occ);
      definitionVariableCount.set(authored.tag, occ + 1);
    }
  }
  return {
    definitionVariableCount,
    definitionConstantTags,
    authoredVariableOccurrenceById,
  };
}

/** A dynamic-Array EXTRA copy's synthetic id (`${sourceId}#arr<n>`) — the
 *  block-evaluator's documented copy-id contract (`authoredOccurrenceForChild`
 *  recovers the source occurrence from it). Such a copy reuses the source
 *  ATTRIB row for value/visibility but rides its OWN translated anchor for
 *  placement (PDF-F18 — `emitVariableAttdef`'s `isArrayCopy`). */
function isArrayCopyId(id: string): boolean {
  return /#arr\d+$/.test(id);
}

/**
 * PDF-F2/F4/F17/F18 — dispatch ONE ATTDEF child (Phase A/B). A CONSTANT ATTDEF
 * renders its own `defaultValue` (Phase B, no occurrence counting); a VARIABLE
 * ATTDEF resolves its AUTHORED occurrence index (BY id, so hidden same-tag
 * siblings and Array copies still hit the right row) and emits the matching
 * ATTRIB value / template default. Shared by the geometry walk AND the guarded
 * (cycle / over-depth) path, so a matching ATTRIB keeps its ATTDEF template in
 * BOTH — the template match is non-recursive (ATTDEFs are leaves), safe to run
 * under the recursion guard.
 */
function emitAttdefChild(
  out: CadPrimitive[],
  attdef: AttdefEntity,
  census: AttdefCensus,
  state: InsertWalkState,
  baseMatrix: Affine2D,
  guarded: boolean,
): void {
  if (attdefFlag(attdef, "constant")) {
    // PDF-F2 — a CONSTANT ATTDEF (DXF group 70 bit 2) has NO per-instance
    // ATTRIB row; its baked `defaultValue` renders at every INSERT. Under the
    // recursion GUARD (cycle / over-depth) there is no LIVE row to surface — the
    // old post-recursion path only re-surfaced actual `child.attributes`, so a
    // cyclic / over-depth block must NOT leak its baked constant label (@codex
    // #480). The unguarded walk still renders it.
    if (guarded) return;
    emitConstantAttdef(out, attdef, state, baseMatrix);
    return;
  }
  // PDF-F4/F17/F18 — the VARIABLE ATTDEF's AUTHORED occurrence index (by id),
  // NOT a running count of visible children, so a surviving ATTDEF whose earlier
  // same-tag sibling was hidden keeps its OWN row (F17) and an Array copy reuses
  // the source row (F18). A synthesized id with no authored occurrence → 0.
  const occurrenceIndex = authoredOccurrenceForChild(
    attdef.id,
    census.authoredVariableOccurrenceById,
  );
  // Under the guard, a MATCHED ATTRIB still resolves via its template (F-guard),
  // but an UNMATCHED variable ATTDEF must NOT fall back to the template default
  // (@codex #480 — `suppressUnmatchedDefault`).
  emitVariableAttdef(
    out,
    attdef,
    occurrenceIndex,
    isArrayCopyId(attdef.id),
    state,
    baseMatrix,
    guarded,
  );
}

/**
 * PDF orphan pass — an `attributes[]` row whose tag matched NO ATTDEF occurrence
 * in this block definition (the ATTDEF was removed/renamed after this INSERT was
 * placed, OR a stray duplicate beyond the last same-tag ATTDEF) still carries a
 * live value; surface it rather than drop it. Mirrors `blockFromEntity.ts`'s
 * orphan pass. A row is skipped (NOT orphaned) when it was consumed by a VISIBLE
 * variable ATTDEF occurrence, when it matches a variable ATTDEF the definition
 * authored but the active dynamic-visibility state HID (else toggling a
 * visibility state would spawn a stray label), or when its tag carries a
 * CONSTANT ATTDEF (whose value is authoritative).
 */
function emitAttributeOrphans(
  out: CadPrimitive[],
  census: AttdefCensus,
  state: InsertWalkState,
  baseMatrix: Affine2D,
): void {
  const attributeOccurrenceSeenByTag = new Map<string, number>();
  for (const attr of state.attributes) {
    const occurrenceIndex = attributeOccurrenceSeenByTag.get(attr.tag) ?? 0;
    attributeOccurrenceSeenByTag.set(attr.tag, occurrenceIndex + 1);
    // The AUTHORED variable-ATTDEF count bounds how many rows the templates
    // claim: each authored occurrence [0, count) is either CONSUMED by a
    // visible ATTDEF or SUPPRESSED because its ATTDEF was authored but hidden by
    // the active visibility state (F17). Array-generated copies REUSE an
    // already-claimed authored row (F18) — they never push this bound past the
    // authored count — so the count alone (not a running visible tally that
    // array copies would inflate) is the correct suppression bound.
    const consumedOrHidden = census.definitionVariableCount.get(attr.tag) ?? 0;
    if (occurrenceIndex < consumedOrHidden) continue;
    if (census.definitionConstantTags.has(attr.tag)) continue;
    emitOrphanAttribute(out, attr, state, baseMatrix);
  }
}

/** Walk a block definition's children under the composed `state.matrix`. */
function walkBlock(
  out: CadPrimitive[],
  blockName: string,
  state: InsertWalkState,
): void {
  const def = state.ctx.blocks[blockName];
  if (!def) {
    // Missing block — no geometry / ATTDEF templates to resolve (renderer
    // warns). The INSERT's OWN `attributes[]` still carry live values the user
    // sees, so surface them as orphans rather than dropping the data (CLAUDE.md
    // "never simplify away data"). With no block definition there is no
    // basePoint to subtract, so the block-origin fallback rides `state.matrix`.
    emitOrphanAttributes(out, state, state.matrix);
    return;
  }

  // Subtract the block base point as part of the local→world transform.
  const baseMatrix = compose(state.matrix, {
    a: 1,
    b: 0,
    c: 0,
    d: 1,
    e: -def.basePoint.x,
    f: -def.basePoint.y,
  });

  // Resolve the children to walk. For STATIC blocks this is `def.entities` by
  // reference (evaluateBlock's no-op fast path) — zero behaviour drift. For
  // DYNAMIC blocks (def.dynamicMetadata present) we run the SAME pure
  // evaluator the renderer uses (apps/web/src/app/scene/blockRender.tsx →
  // evaluateBlock) so the PLOT reflects parameter-driven transforms + the
  // active visibility state, not the authored default / hidden geometry (#177
  // P2). evaluateBlock is pure (no WebGL / scene coupling) so it is safe in the
  // headless PDF backend.
  const children = resolveBlockChildren(def, state);

  // PDF-F2/F3/F4/F17/F18 — Phase A/B ATTDEF census (mirroring the typed
  // reference `blockFromEntity.ts flattenTypedInsert`), keyed off the FULL
  // authored `def.entities` (BEFORE the dynamic-visibility filter).
  const census = buildAttdefCensus(def);

  // GUARD (over-depth OR a cycle): stop GEOMETRY descent only. Attribute
  // resolution is NON-recursive (ATTDEFs are leaves; no nested Block descent),
  // so it STILL runs against the available definition below — a matching ATTRIB
  // keeps its ATTDEF anchor/style/height/defaults instead of degrading to an
  // orphan at the block origin, exactly like the pre-refactor post-recursion
  // attribute loop that still looked up `ctx.blocks[blockName]` for template
  // data after stopping descent. The guard never trips at the top level
  // (depth 0 / empty visited), so this never double-emits a real INSERT's
  // attributes.
  const geometryGuarded =
    state.depth >= MAX_DEPTH || state.visited.has(blockName);

  if (!geometryGuarded) {
    const nextVisited = new Set(state.visited);
    nextVisited.add(blockName);
    // Interleave ATTDEF resolution + geometry in authored order (byte-identical
    // to the pre-extraction single loop): an ATTDEF child emits its resolved
    // value in place; every other child descends via `convertChild`.
    for (const child of children) {
      if (child.type === "Attdef") {
        emitAttdefChild(out, child, census, state, baseMatrix, false);
        continue;
      }
      convertChild(out, child, {
        ...state,
        matrix: baseMatrix,
        visited: nextVisited,
      });
    }
  } else {
    // Guarded — resolve ONLY the ATTDEF children (no geometry / nested descent),
    // so a MATCHED ATTRIB in a cyclic / over-depth block still rides its
    // template (F-guard) rather than an orphan block-origin fallback. An
    // UNMATCHED variable ATTDEF (template default) and every CONSTANT ATTDEF are
    // SUPPRESSED here (`guarded=true`): the old post-recursion path surfaced only
    // actual `child.attributes`, so a guarded re-entry must not leak spurious
    // default / constant labels for a block with no matching rows (@codex #480).
    for (const child of children) {
      if (child.type === "Attdef") {
        emitAttdefChild(out, child, census, state, baseMatrix, true);
      }
    }
  }

  // Orphan pass — runs under BOTH regimes (a genuinely orphaned row surfaces its
  // value whether or not geometry descent was guarded). Block-local orphans ride
  // `baseMatrix`; a WORLD `overridePosition` still rides `state.outer`.
  emitAttributeOrphans(out, census, state, baseMatrix);
}

/**
 * Resolve a block definition's effective children for the given INSERT
 * instance. Static blocks return `def.entities` verbatim; dynamic blocks
 * (carrying `dynamicMetadata`) are evaluated via the shared pure
 * `evaluateBlock` runtime so parameter actions + visibility filtering match
 * what the user sees on screen (#177 P2).
 */
function resolveBlockChildren(
  def: BlockDefinition,
  state: InsertWalkState,
): readonly BlockChildEntity[] {
  if (!def.dynamicMetadata) {
    // Static block — no evaluation; preserve the exact pre-fix behaviour.
    return def.entities;
  }
  const result = evaluateBlock({
    blockDef: def,
    instance: {
      customProperties: state.instance.customProperties ?? {},
      ...(state.instance.visibilityState !== undefined
        ? { visibilityState: state.instance.visibilityState }
        : {}),
    },
    blockDefinitions: state.ctx.blocks,
  });
  // evaluateBlock preserves BlockChildEntity shape (it mutates copies +
  // filters by visibility) — same cast the renderer uses for its resolved set.
  return result.entities as unknown as readonly BlockChildEntity[];
}

/** Convert one block child entity under the composed matrix. */
function convertChild(
  out: CadPrimitive[],
  child: BlockChildEntity,
  state: InsertWalkState,
): void {
  switch (child.type) {
    case "Line": {
      const style = childStyle(child, state);
      // PDF-6.3b — CTB/STB do-not-plot honour (per-child; a block whose
      // children land on different ACI bins may skip some and keep others).
      if (style.skip) return;
      const layer = resolveChildLayer(child.layer, state.insertLayer);
      const start = apply(state.matrix, child.start);
      const end = apply(state.matrix, child.end);
      out.push(
        pathPrim(state.parentId, layer, start, [{ kind: "line", to: end }], false, style),
      );
      return;
    }
    case "LWPOLYLINE": {
      const style = childStyle(child, state);
      if (style.skip) return;
      const layer = resolveChildLayer(child.layer, state.insertLayer);
      const verts = child.vertices;
      if (verts.length < 2) return;
      // A wide LWPOLYLINE child plots as the SAME filled band the canvas
      // draws, offset in the block's LOCAL frame and then mapped through the
      // block affine — so a scaled / sheared block scales the band with it,
      // which a scalar half-width applied after the transform could not do.
      const band = lwpolylineBand(child, state.matrix);
      if (band) {
        out.push(
          bandPathPrimitive(
            state.parentId,
            layer,
            band,
            style,
            // A block child plots in the SAME document as its INSERT, so it
            // reads the same `$FILLMODE`. `state.ctx` is the sheet context
            // `childStyle` spreads from, so there is one value, not two.
            state.ctx.fillmode,
          ),
        );
        return;
      }
      const start = apply(state.matrix, { x: verts[0]!.x, y: verts[0]!.y });
      const segments: PathSegment[] = [];
      for (let i = 0; i < verts.length - 1; i++) {
        appendTransformedBulgeSpan(
          segments,
          verts[i]!,
          verts[i + 1]!,
          verts[i]!.bulge,
          state.matrix,
        );
      }
      if (child.closed) {
        const last = verts[verts.length - 1]!;
        appendTransformedBulgeSpan(
          segments,
          last,
          verts[0]!,
          last.bulge,
          state.matrix,
        );
      }
      out.push(pathPrim(state.parentId, layer, start, segments, child.closed, style));
      return;
    }
    case "Circle": {
      const style = childStyle(child, state);
      if (style.skip) return;
      const layer = resolveChildLayer(child.layer, state.insertLayer);
      const { start, segments } = transformedCircle(
        child.center,
        child.radius,
        0,
        Math.PI * 2,
        true,
        state.matrix,
      );
      out.push(pathPrim(state.parentId, layer, start, segments, true, style));
      return;
    }
    case "Arc": {
      const style = childStyle(child, state);
      if (style.skip) return;
      const layer = resolveChildLayer(child.layer, state.insertLayer);
      // Mirror (negative determinant) flips orientation; the affine bezier
      // eval handles this automatically (it maps the local-CCW arc through
      // the affine, so a mirrored block traces the visually-flipped arc).
      const { start, segments } = transformedCircle(
        child.center,
        child.radius,
        child.startAngle * DEG_TO_RAD,
        child.endAngle * DEG_TO_RAD,
        true, // DXF ARC always CCW in local space
        state.matrix,
      );
      out.push(pathPrim(state.parentId, layer, start, segments, false, style));
      return;
    }
    case "Ellipse": {
      const style = childStyle(child, state);
      if (style.skip) return;
      const layer = resolveChildLayer(child.layer, state.insertLayer);
      const { start, segments } = transformedEllipse(child, state.matrix);
      const isFull =
        Math.abs(child.endParam - child.startParam) >= Math.PI * 2 - 1e-9;
      out.push(pathPrim(state.parentId, layer, start, segments, isFull, style));
      return;
    }
    case "Block":
      convertNestedInsert(out, child, state);
      return;
    case "Table":
      // ACAD_TABLE inside a block definition. Its geometry is its anonymous
      // `*T<N>` graphics block, so it descends the SAME nested-block-reference
      // walk through the ONE shared `tableAsBlockReference` projection
      // `cad-core`'s select / bounds resolve (@codex PR #785 round 2, F1).
      convertNestedInsert(out, tableAsBlockReference(child), state);
      return;
    case "Polyline": {
      // The legacy HEAVY polyline inside a block (@codex PR #785 round 2, F1).
      // Its subtype-gated geometry — 2D bulge arcs at the ENTITY elevation
      // through its own OCS lift, a 3D polyline's WCS vertex run, and a
      // polygon / poly-face mesh's wire runs from the shared
      // `polylineMeshWireRuns` topology — is built ONCE by
      // `heavyPolylineConverter.ts` and mapped through the block affine there,
      // so a block child and a top-level polyline can never disagree. A mesh
      // yields SEVERAL disjoint runs, hence a path per run.
      const style = childStyle(child, state);
      if (style.skip) return;
      const layer = resolveChildLayer(child.layer, state.insertLayer);
      // Width band first, for the same reason and through the same shared
      // loops as the LWPOLYLINE arm above (a mesh subtype never has one).
      const band = heavyPolylineBandPath(child, state.matrix);
      if (band) {
        out.push(
          bandPathPrimitive(
            state.parentId,
            layer,
            band,
            style,
            state.ctx.fillmode,
          ),
        );
        return;
      }
      for (const path of heavyPolylinePaths(child, state.matrix)) {
        out.push(
          pathPrim(
            state.parentId,
            layer,
            path.start,
            path.segments,
            path.closed,
            style,
          ),
        );
      }
      return;
    }
    case "OLE Frame": {
      // The frame RECTANGLE only — the embedded EMF raster is deliberately not
      // rendered anywhere in this project (`oleFrameConverter.ts`). Under an
      // oblique block rotation the frame is a ROTATED rectangle, so all FOUR
      // corners are mapped through the affine; mapping only the two stored
      // opposite corners would emit the axis-aligned box around the diagonal.
      const style = childStyle(child, state);
      if (style.skip) return;
      const corners = oleFramePlotCorners(child, state.matrix);
      if (!corners) return;
      const layer = resolveChildLayer(child.layer, state.insertLayer);
      out.push(
        pathPrim(
          state.parentId,
          layer,
          corners[0]!,
          corners.slice(1).map((to) => ({ kind: "line" as const, to })),
          true,
          style,
        ),
      );
      return;
    }
    case "Attdef":
      // ATTDEF (block-definition attribute placeholder) is resolved by
      // `walkBlock`'s Phase A/B loop (constant → own `defaultValue`, variable →
      // matching ATTRIB value / template default), never here — the generic
      // dispatch skips it. Kept as a defensive no-op in case a future caller
      // routes an ATTDEF through `convertChild` directly.
      return;
    case "Text":
    case "MText":
      // Standalone block text — recorded as a text run (PDF-2 renders).
      out.push(textChildToRun(child, state));
      return;
    case "Spline": {
      // PDF-5c: build the LOCAL-space cubic beziers from the SAME pure math
      // the renderer + top-level converter use, then map every control point
      // through the block affine. An affine maps cubic-bezier control points
      // exactly (a degree-3 polynomial composed with an affine stays degree-3
      // with the transformed control polygon), so a scaled/rotated/mirrored
      // block spline plots correctly — no re-sampling, no fidelity loss.
      const style = childStyle(child, state);
      if (style.skip) return;
      const layer = resolveChildLayer(child.layer, state.insertLayer);
      const localSegments = splineBezierSegments(child);
      if (localSegments.length === 0) return;
      const first = localSegments[0]!;
      const start = apply(state.matrix, { x: first.p0.x, y: first.p0.y });
      const segments: PathSegment[] = localSegments.map((s) => {
        const c1 = apply(state.matrix, { x: s.cp1.x, y: s.cp1.y });
        const c2 = apply(state.matrix, { x: s.cp2.x, y: s.cp2.y });
        const to = apply(state.matrix, { x: s.p3.x, y: s.p3.y });
        return { kind: "bezier" as const, c1, c2, to };
      });
      out.push(
        pathPrim(state.parentId, layer, start, segments, child.closed === true, style),
      );
      return;
    }
    case "Point":
      // POINT marker rendering (PDMODE) deferred. Emit nothing.
      return;
    default:
      return;
  }
}

/**
 * Walk a nested BLOCK REFERENCE child under the running transform.
 *
 * Shared by `convertChild`'s `Block` arm and its `Table` arm (@codex PR #785
 * round 2, F1): an `ACAD_TABLE` IS a block reference to its anonymous `*T<N>`
 * graphics block (first subclass `AcDbBlockReference`), projected through the
 * ONE shared `tableAsBlockReference` view, so it must descend through exactly
 * this composition — a second nested-insert walk would be free to drift from
 * it on OCS parity / BYBLOCK colour / layer-0 inheritance.
 */
function convertNestedInsert(
  out: CadPrimitive[],
  insert: BlockInsertEntity,
  state: InsertWalkState,
): void {
  // Nested INSERT — compose the child's transform onto the running matrix.
  // PDF-F6: a nested INSERT with a non-default extrusion has its whole
  // placement (insertion + scale + rotation) authored in its OWN OCS plane,
  // exactly like a top-level INSERT (`insertToPrimitives`'s `ocsOuter`).
  // Lift it to WCS with the SAME OCS basis map, composed OUTERMOST over the
  // child's insert affine (so `world = state.matrix · E · insertMatrix`).
  // Default (0,0,1) normal → `ocsOuterMatrix` returns IDENTITY, so this is
  // byte-identical to the prior bare `insertMatrix` for planar nested blocks.
  const nestedMatrix = compose(
    state.matrix,
    compose(
      ocsOuterMatrix(insert.extrusion),
      insertMatrix(
        { x: insert.insertionPoint.x, y: insert.insertionPoint.y },
        insert.scaleX,
        insert.scaleY,
        insert.rotation,
        { x: 0, y: 0 },
      ),
    ),
  );
  // PDF-F6 scalar twin — the child OCS lift's SCALAR rotation / handedness
  // for the nested block's OWN block-local attribute text (its anchor rides
  // `nestedMatrix`, which includes this OCS basis, so the glyph angle must
  // too — @codex #480). {0,false} for a planar (default-normal) nested block.
  const nestedOcsScalar = ocsScalarContribution(insert.extrusion);
  const nestedColor = resolveChildHexForBlock(insert, state);
  const nestedLinetype = resolveChildLinetypeForBlock(insert, state);
  // PDF-6.2a: a nested INSERT on layer "0" inherits the parent INSERT's
  // resolved layer; otherwise it keeps its own. ITS children then inherit
  // this resolved layer (propagation through the hierarchy).
  const nestedLayer = resolveChildLayer(insert.layer, state.insertLayer);
  // PDF-F16: propagate the CTB ACI like the colour — a nested ByBlock INSERT
  // inherits the parent's ACI, an explicit / ByLayer nested INSERT
  // contributes its own / its (effective) layer's index.
  const nestedAci = resolveChildAciForBlock(insert, nestedLayer, state);
  walkBlock(out, insert.blockName, {
    ...state,
    matrix: nestedMatrix,
    depth: state.depth + 1,
    blockColor: nestedColor,
    blockAci: nestedAci,
    blockLinetype: nestedLinetype,
    insertLayer: nestedLayer,
    // Nested INSERT carries its OWN dynamic params (DYN-3): evaluate the
    // nested block with the child's customProperties / visibilityState.
    instance: {
      customProperties: insert.customProperties ?? {},
      ...(insert.visibilityState !== undefined
        ? { visibilityState: insert.visibilityState }
        : {}),
    },
    // PDF-F5 — the nested INSERT's OWN attributes[] (NOT the enclosing
    // chain's), resolved against the nested block's ATTDEFs inside the
    // recursive `walkBlock`. A WORLD `overridePosition` on them lives in
    // THIS (enclosing) block's local frame, so its residual is the
    // enclosing `baseMatrix` (`state.matrix` here), NOT the nested
    // `nestedMatrix` (running it through `nestedMatrix` would double-transform
    // — the F1 defect for the nested case).
    attributes: insert.attributes ?? [],
    outer: state.matrix,
    // PDF-F10 — accumulate the authored rotation SUM + scale-sign products
    // for the nested block's OWN block-local attribute text, exactly like
    // the typed oracle's nested recursion (`blockFromEntity.ts`:
    // `rotationDeg + child.rotation`, `scale.x · child.scaleX`). PDF-F6 —
    // the child's OWN OCS lift first composes its basis rotation /
    // handedness (`nestedOcsScalar`) with the child's OWN local rotation
    // (adds under a proper rotation, REFLECTS under a mirror —
    // `composeOcsRotationDeg`, @codex #507 round-3) to get the child's
    // self-contained composed angle (`childLocalRotationDeg`). THAT then
    // folds onto the accumulated outer chain the SAME way — ADDS if the
    // chain accumulated so far (`state.ocsMirrorParity`, exclusive of the
    // unrelated scale-sign mirror) is NOT already OCS-mirrored, REFLECTS
    // if it is (a reflection conjugates a following rotation to its
    // inverse: `F·R(θ) ≡ R(−θ)·F` — @codex #507 round-4). A single-OCS-
    // level chain (the overwhelmingly common case, incl. every existing
    // round-3 test) starts from `ocsMirrorParity = false`, so this reduces
    // to the prior plain `state.rotationSumDeg + childLocalRotationDeg`
    // sum — only a SECOND stacked OCS mirror (an already-mirrored OUTER
    // nesting an OCS-tilted INNER) diverges from the pre-fix unconditional
    // add, which put a mirrored-parent's nested OCS attribute text 180°
    // off. The nested INSERT's WORLD attributes ride the ENCLOSING
    // residual (`state.matrix`, no child OCS), whose scalar rotation is
    // THIS (enclosing) level's fold — so `outerScalarRotationDeg` stays
    // unchanged.
    rotationSumDeg: composeOcsRotationDeg(
      composeOcsRotationDeg(insert.rotation, nestedOcsScalar),
      {
        rotationDeg: state.rotationSumDeg,
        flipHandedness: state.ocsMirrorParity,
      },
    ),
    scaleXNeg: state.scaleXNeg !== insert.scaleX < 0,
    // PDF-F6 — the child OCS handedness flip toggles the Y sign (its residual
    // mirror after the atan2 X-angle is always the Y axis), matching the
    // top-level rule above (@codex #507).
    scaleYNeg:
      (state.scaleYNeg !== insert.scaleY < 0) !==
      nestedOcsScalar.flipHandedness,
    outerScalarRotationDeg: foldScalarRotationDeg(
      state.rotationSumDeg,
      state.scaleXNeg,
      state.scaleYNeg,
    ),
    // @codex #507 round-4 — accumulate ONLY the OCS-basis mirror source
    // (independent per-level XOR, same shape as `scaleXNeg`/`scaleYNeg`'s
    // chains, deliberately excluding the child's own scale-sign flips —
    // those feed `scaleYNeg` above and follow the UNRELATED raw-sum
    // convention `insertAttributes.ts foldScalarRotationDeg` documents).
    ocsMirrorParity: state.ocsMirrorParity !== nestedOcsScalar.flipHandedness,
  });
}

/** Build a transformed ellipse-arc by mapping its parametric curve through `m`. */
function transformedEllipse(
  child: Extract<BlockChildEntity, { type: "Ellipse" }>,
  m: Affine2D,
): { start: Point2; segments: PathSegment[] } {
  const majorMag = Math.hypot(
    child.majorAxisEndpoint.x,
    child.majorAxisEndpoint.y,
  );
  const minorMag = majorMag * child.ratio;
  const cosF = majorMag === 0 ? 1 : child.majorAxisEndpoint.x / majorMag;
  const sinF = majorMag === 0 ? 0 : child.majorAxisEndpoint.y / majorMag;
  let sweep = child.endParam - child.startParam;
  if (sweep <= 0) sweep += Math.PI * 2;
  if (Math.abs(child.endParam - child.startParam) >= Math.PI * 2 - 1e-9) {
    sweep = Math.PI * 2;
  }
  const local = (t: number): Point2 => {
    const lx = majorMag * Math.cos(t);
    const ly = minorMag * Math.sin(t);
    return {
      x: child.center.x + lx * cosF - ly * sinF,
      y: child.center.y + lx * sinF + ly * cosF,
    };
  };
  const localDeriv = (t: number): Point2 => {
    const dlx = -majorMag * Math.sin(t);
    const dly = minorMag * Math.cos(t);
    return {
      x: dlx * cosF - dly * sinF,
      y: dlx * sinF + dly * cosF,
    };
  };
  const evalPoint = (t: number): Point2 => apply(m, local(t));
  const evalDeriv = (t: number): Point2 => applyLinear(m, localDeriv(t));
  return arcSpansToBeziers(child.startParam, sweep, evalPoint, evalDeriv);
}

/** Resolve a child entity's plot style under the BYBLOCK context. */
function childStyle(
  child: BlockChildEntity,
  state: InsertWalkState,
): PlotStyle {
  const ctx: CadConvertContext = {
    ...state.ctx,
    blockColor: state.blockColor,
    // PDF-F16 — a BYBLOCK geometry child inherits the enclosing INSERT's ACI so
    // its do-not-plot / screening CTB entry follows the INSERT colour (its own
    // resolved hex lost the index).
    blockAci: state.blockAci,
    blockLinetype: state.blockLinetype,
  };
  // EPIC-COLOR-UNION S1 — `BlockChildEntity` carries the full common-property
  // set (layer / color:EntityColor / linetype / lineweight / linetypeScale /
  // transparency) that `resolvePlotStyle`'s `StyledEntity` requires, so pass it
  // directly instead of the prior `{color: string}` structural cast.
  return resolvePlotStyle(child, ctx);
}

/**
 * Resolve an INSERT's OWN linetype name for the BYBLOCK chain (PDF-5c). A
 * ByLayer INSERT contributes its layer's assigned linetype; ByBlock falls
 * through to the parent block's linetype (already resolved on the context);
 * an explicit token passes through. Continuous / sentinel → null so the dash
 * registry short-circuits to solid for ByBlock children.
 */
function resolveInsertLinetype(
  insert: { linetype?: string; layer: string },
  ctx: CadConvertContext,
): string | null {
  const token = (insert.linetype ?? "ByLayer").trim();
  const upper = token.toUpperCase();
  let name: string;
  if (upper === "BYLAYER" || token === "") {
    name = ctx.layerLinetypes?.get(insert.layer.toLowerCase()) ?? "Continuous";
  } else if (upper === "BYBLOCK") {
    name = ctx.blockLinetype ?? "Continuous";
  } else {
    name = token;
  }
  return name.toUpperCase() === "CONTINUOUS" ? null : name;
}

/** Resolve the linetype a nested INSERT contributes to its own children. */
function resolveChildLinetypeForBlock(
  child: Extract<BlockChildEntity, { type: "Block" }>,
  state: InsertWalkState,
): string | null {
  return resolveInsertLinetype(child, {
    ...state.ctx,
    blockLinetype: state.blockLinetype,
  });
}

/**
 * Resolve an INSERT's OWN colour to a concrete `#rrggbb` hex string for the
 * BYBLOCK chain. Passing the raw keyword ("ByLayer"/"ByBlock") as `blockColor`
 * is wrong — `resolveColorHex` cannot parse a keyword as a literal parent
 * colour, so a BYBLOCK child mis-resolves (#177 P2). Resolving here through
 * the same chain (layer colour for ByLayer, parent block colour for ByBlock)
 * yields a literal hex the child's BYBLOCK lookup can inherit.
 */
function resolvePlotRgbHex(
  entityColor: string | undefined,
  layer: string,
  ctx: CadConvertContext,
): string {
  return resolveColorHex(entityColor, layer, ctx.layerColors, ctx.blockColor);
}

/** Resolve the colour a nested INSERT contributes to its own children. */
function resolveChildHexForBlock(
  child: Extract<BlockChildEntity, { type: "Block" }>,
  state: InsertWalkState,
): string | null {
  // A nested INSERT with an explicit colour overrides BYBLOCK for its
  // children; BYBLOCK ("BYBLOCK"/ACI "0"/empty) falls through to the parent's
  // block colour (already a resolved hex). FIX (#177 P2): an explicit colour is
  // resolved to a concrete hex (it may be an ACI index like "1" which
  // resolveColorHex parses, but a defensive resolve keeps the contract — pass
  // the literal through resolveColorHex which returns it unchanged when already
  // concrete). EPIC-COLOR-UNION S1 — project the typed union to the legacy
  // string the BYBLOCK chain inspects + resolves (byte-identical for
  // ByLayer/ByBlock/index).
  const childColorStr = colorToLegacyString(coerceColor(child.color));
  const c = childColorStr.trim().toUpperCase();
  if (c === "" || c === "BYBLOCK" || c === "0") {
    return state.blockColor;
  }
  // ByLayer does NOT defer to the parent block colour — a nested INSERT set to
  // ByLayer takes ITS OWN layer's colour (AutoCAD parity), which its ByBlock
  // children then inherit. Resolve it against the nested INSERT's EFFECTIVE
  // layer (`resolveChildLayer`: layer "0" → the enclosing INSERT's inherited
  // layer), NOT the raw parent colour — else a nested attribute whose ATTDEF is
  // ByBlock (or an orphan ATTRIB) plots the PARENT colour instead of the nested
  // INSERT's layer colour. An explicit ACI/hex is resolved to a concrete hex so
  // the child's BYBLOCK chain inherits a parseable literal, not a raw token.
  const childLayer = resolveChildLayer(child.layer, state.insertLayer);
  return resolveColorHex(
    childColorStr,
    childLayer,
    state.ctx.layerColors,
    state.blockColor,
  );
}

/**
 * PDF-F16 — resolve the CTB ACI a nested INSERT contributes to its own children,
 * the index twin of {@link resolveChildHexForBlock}. `entityAciForCtb` already
 * encodes the same BYBLOCK / explicit / ByLayer chain: a ByBlock nested INSERT
 * ("0") inherits the parent ACI (`state.blockAci`, threaded as the fallback), an
 * explicit ACI contributes itself, a ByLayer nested INSERT contributes its
 * EFFECTIVE layer's ACI (layer "0" → the inherited INSERT layer, precomputed as
 * `childLayer`). Null when the index cannot be determined (a hex literal, a
 * non-ACI layer colour) — the child's own plot style then stands.
 */
function resolveChildAciForBlock(
  child: Extract<BlockChildEntity, { type: "Block" }>,
  childLayer: string,
  state: InsertWalkState,
): number | null {
  return entityAciForCtb(
    colorToLegacyString(coerceColor(child.color)),
    childLayer,
    state.ctx.layerColors,
    state.blockAci,
  );
}

/** Build a stroked path primitive. */
function pathPrim(
  entityId: string,
  layer: string,
  start: Point2,
  segments: PathSegment[],
  closed: boolean,
  style: PlotStyle,
): PathPrimitive {
  return {
    type: "path",
    entityId,
    layer,
    start,
    segments,
    closed,
    fill: "none",
    style,
  };
}

// Block-child TEXT / MTEXT → text-run conversion (`textChildToRun`) now lives
// in `./insertTextChild.ts` (CLAUDE.md file-size guardrail, PDF-TEXT-CHILD-
// GEOMETRY) — it applies the SAME block-local scalar transform
// (`InsertWalkState.rotationSumDeg`/`scaleXNeg`/`scaleYNeg`, folded via
// `insertAttributes.ts foldScalarRotationDeg`) that `resolveAttrPlacement`
// already applies to block-local ATTDEF/ATTRIB text (@codex #507), instead of
// the prior verbatim height/rotation/widthFactor pass-through.
